import React from 'react';
import { Tag, Tooltip } from 'antd';
import { AndroidFilled, AppleFilled, DownloadOutlined, LockFilled, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/fr';
import { formatPhoneNumber } from '@leedpulse-monorepo/utils';
import { AI_RECEPTIONIST_VIOLET, CATEGORICAL_COLORS } from './chartColors';

// `commonUtils` ne pose `dayjs.locale('fr')` qu'à l'intérieur d'une de ses
// fonctions : rien ne garantit qu'elle ait été appelée avant ce rendu, et le
// tableau affichait « 3 minutes ago ». On charge la locale et le greffon ici,
// au niveau du module, là où on s'en sert.
dayjs.extend(relativeTime);
dayjs.locale('fr');

// Cellules communes aux deux tables d'appels de l'onglet Général
// (`LastCallsTable`, `FaultyCallsPanel`). Une plateforme ou une direction doit
// s'afficher pareil dans les deux : les dupliquer, c'est accepter qu'elles
// divergent le jour où une nouvelle valeur apparaît.

// Valeurs brutes vues côté backend, mêmes clés que `BreakdownPanel`. Une
// valeur absente de cette table (nouvelle plateforme) tombe sur le fallback
// de `platformLabel` et s'affiche telle quelle plutôt que de disparaître.
const PLATFORM_LABELS: Record<string, string> = {
  callr: 'GSM',
  livekit: 'VoIP',
  elevenlabs_receptionist: 'Réceptionniste IA',
};

export const platformLabel = (platform: string): string => PLATFORM_LABELS[platform] ?? platform;

// Mêmes couleurs que les donuts de la Répartition : une plateforme se
// reconnaît à sa couleur, elle ne doit pas en changer d'un bloc à l'autre.
const PLATFORM_COLORS: Record<string, string> = {
  livekit: CATEGORICAL_COLORS[0],
  callr: CATEGORICAL_COLORS[1],
  elevenlabs_receptionist: AI_RECEPTIONIST_VIOLET,
};

export const renderPlatform = (platform: string | null): React.ReactNode => {
  if (!platform) return '-';
  return <Tag color={PLATFORM_COLORS[platform]}>{platformLabel(platform)}</Tag>;
};

// `Platform.OS` tel qu'écrit par l'app mobile, mêmes marques que la Répartition.
const DEVICE_ICONS: Record<string, React.ReactNode> = {
  ios: <AppleFilled />,
  android: <AndroidFilled />,
};

const DEVICE_LABELS: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
};

/**
 * Vide sur un appel GSM, et c'est la vérité : il ne passe pas par l'app, aucun
 * appareil n'y est attaché.
 */
export const renderDevice = (device: string | null): React.ReactNode => {
  if (!device) return '-';
  const icon = DEVICE_ICONS[device];
  if (!icon) return device;
  return <Tooltip title={DEVICE_LABELS[device] ?? device}>{icon}</Tooltip>;
};

// `formatPhoneNumber` renvoie la valeur brute si `libphonenumber-js` ne
// parvient pas à parser le numéro (numéro non français, format inattendu) :
// jamais de crash, jamais de cellule vide.
export const formatCallNumber = (value: string | null): string => {
  if (!value) return '-';
  return formatPhoneNumber(value);
};

/**
 * Côté Leedflow d'un appel ou d'un SMS : le titulaire se lit mieux que le nom
 * d'abonnement, souvent long, et que le numéro. Les deux restent en infobulle.
 * Sans titulaire, le nom d'abonnement ; sans l'un ni l'autre, le numéro.
 */
export const renderLine = (
  line: { holderName: string | null; subscriptionName: string | null; sender: string | null },
): React.ReactNode => {
  const label = line.holderName || line.subscriptionName;
  if (!label) return formatCallNumber(line.sender);
  const details = [line.holderName ? line.subscriptionName : null, formatCallNumber(line.sender)].filter(Boolean).join(' · ');
  return <Tooltip title={details}>{label}</Tooltip>;
};

/**
 * Le contact d'en face est marqué confidentiel par son propriétaire : ni
 * synthèse d'appel ni message ne sont consultables ici. Même marqueur dans les
 * deux tables, pour la même raison.
 *
 * Un cadenas seul, pas un tag libellé : la colonne porte déjà un numéro ou un
 * message, un mot de plus par ligne mangerait la largeur. Le jaune est celui du
 * `colorWarning` d'antd, donc du bandeau du détail d'appel.
 */
export const renderConfidential = (): React.ReactNode => (
  <Tooltip title="Contact marqué confidentiel : son contenu n'est pas consultable">
    <LockFilled style={{ color: '#FAAD14' }} aria-label="Contact confidentiel" />
  </Tooltip>
);

export const renderDirection = (direction: string | null): React.ReactNode => {
  if (direction === 'inbound') {
    return <Tag icon={<DownloadOutlined />} color="blue">Entrant</Tag>;
  }
  if (direction === 'outbound') {
    return <Tag icon={<UploadOutlined />} color="green">Sortant</Tag>;
  }
  return '-';
};

/**
 * Temps relatif — « il y a 3 minutes » se lit sans calcul mental sur un
 * tableau d'activité récente. L'horodatage exact reste en infobulle : le
 * relatif est figé au rendu et vieillit jusqu'au prochain rafraîchissement.
 */
export const renderDate = (created: number): React.ReactNode => {
  if (!created) return '-';
  const date = dayjs(created);
  return <Tooltip title={date.format('DD/MM/YYYY HH:mm:ss')}>{date.fromNow()}</Tooltip>;
};
